import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import LanguageSelector from '../components/LanguageSelector'
import { useTranslation } from '../hooks/useTranslation'
import '../pages/LoginPage.css'

function LoginPage() {
  const [isLogin, setIsLogin] = useState(true)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  
  const { login, signup } = useAuth()
  const navigate = useNavigate()
  const glassFormRef = useRef(null)
  const { t } = useTranslation()

  // Charger les identifiants sauvegardés si "Se souvenir de moi" était activé
  useEffect(() => {
    const savedEmail = localStorage.getItem('remembered_email')
    const wasRemembered = localStorage.getItem('remember_me') === 'true'
    localStorage.removeItem('remembered_pwd')
    if (savedEmail && wasRemembered) {
      setEmail(savedEmail)
      setRememberMe(true)
    }
  }, [])

  // Génération des styles dynamiques pour les gouttes de pluie (selon le code original)
  useEffect(() => {
    const styleId = 'rain-animations'
    const existingStyle = document.getElementById(styleId)
    if (existingStyle) {
      existingStyle.remove()
    }
    
    const styleSheet = document.createElement('style')
    styleSheet.id = styleId
    styleSheet.type = 'text/css'
    
    let cssRules = ''
    for (let i = 1; i <= 500; i++) {
      // Génération aléatoire selon le code original SCSS
      const opacity = Math.floor(Math.random() * 90) * 0.01  // random(90) * 0.01
      const left = Math.floor(Math.random() * 1200) * 0.1    // random(1200) * 0.1vw
      const borderLeftWidth = Math.floor(Math.random() * 80) * 0.1  // random(80) * 0.1vmin
      const duration = Math.floor(Math.random() * 15) * 0.15  // random(15) * 0.15s
      const delay = Math.floor(Math.random() * 25) * -0.5     // random(25) * -0.5s
      const midpoint = Math.floor(Math.random() * 50) / 500  // percentage(random(50) / 500)

      cssRules += `
        .drop:nth-child(${i}) {
          opacity: ${opacity};
          left: ${left}vw;
          border-left-width: ${borderLeftWidth}vmin;
          animation: fall-${i} ${duration}s ${delay}s ease-in infinite;
        }
        
        @keyframes fall-${i} {
          ${(midpoint * 100).toFixed(2)}% {
            transform: rotate(var(--angle)) translateX(0);
          }
          100% {
            transform: rotate(var(--angle)) translateX(calc(100vh + 5vmin));
          }
        }
      `
    }
    
    styleSheet.textContent = cssRules
    document.head.appendChild(styleSheet)
    
    return () => {
      const styleToRemove = document.getElementById(styleId)
      if (styleToRemove) {
        styleToRemove.remove()
      }
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    if (isLogin) {
      const result = await login(email, password, rememberMe)
      if (result.success) {
        // Sauvegarder ou supprimer les identifiants selon "Se souvenir de moi"
        if (rememberMe) {
          localStorage.setItem('remembered_email', email)
          localStorage.setItem('remember_me', 'true')
        } else {
          localStorage.removeItem('remembered_email')
          localStorage.removeItem('remember_me')
        }
        localStorage.removeItem('remembered_pwd')
        navigate('/carts')
      } else {
        setError(result.error)
      }
    } else {
      if (password !== confirmPassword) {
        setError('Les mots de passe ne correspondent pas')
        setLoading(false)
        return
      }
      if (password.length < 8) {
        setError('Le mot de passe doit contenir au moins 8 caractères')
        setLoading(false)
        return
      }
      const result = await signup(email, password)
      if (result.success) {
        setError('')
        setIsLogin(true)
        setError('Compte créé avec succès. Connectez-vous maintenant.')
      } else {
        setError(result.error)
      }
    }
    setLoading(false)
  }

  const handleTabSwitch = (toLogin) => {
    if ((toLogin && isLogin) || (!toLogin && !isLogin)) return
    
    setIsLogin(toLogin)
    setError('')
  }

  // Génération des gouttes de pluie
  const rainDrops = Array.from({ length: 500 }, (_, i) => i + 1)

  return (
    <div className="login-container">
      {/* SVG Filter for Glass Distortion */}
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <defs>
          <filter id="glass-distortion">
            <feTurbulence type="turbulence" baseFrequency="0.008" numOctaves="2" result="noise" />
            <feDisplacementMap in="SourceGraphic" in2="noise" scale="77" />
          </filter>
        </defs>
      </svg>

      {/* Effet de pluie CSS - Sans zones interactives */}
      <div className="rain">
        {rainDrops.map((i) => (
          <div key={i} className="drop"></div>
        ))}
      </div>

      <div className="glass-form" ref={glassFormRef}>
        <div className="glass-filter"></div>
        <div className="glass-overlay"></div>
        <div className="glass-specular"></div>
        
        <div className="glass-content">
          <div className="login-header" style={{ position: 'relative' }}>
            <h1>{t('welcome')}</h1>
            <div style={{ position: 'absolute', top: 0, right: 0 }}>
              <LanguageSelector position="right" />
            </div>
          </div>

          <div className="login-tabs">
            <button
              className={`tab ${isLogin ? 'active' : ''}`}
              onClick={() => handleTabSwitch(true)}
            >
              {t('login')}
            </button>
            <button
              className={`tab ${!isLogin ? 'active' : ''}`}
              onClick={() => handleTabSwitch(false)}
            >
              {t('signup')}
            </button>
          </div>

          {error && (
            <div className={error.includes('succès') ? 'success-message' : 'error-message'}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className={`form-group-conditional ${!isLogin ? 'show' : 'hide'}`}>
              <div className="form-group">
                <input
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder="Nom d'utilisateur (optionnel)"
                />
              </div>
            </div>

            <div className="form-group">
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                enterKeyHint="next"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('email')}
                required
              />
            </div>

            <div className="form-group">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete={isLogin ? 'current-password' : 'new-password'}
                autoCapitalize="none"
                spellCheck={false}
                enterKeyHint={isLogin ? 'go' : 'next'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isLogin ? t('password') : t('passwordMin')}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              >
                <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'} aria-hidden="true"></i>
              </button>
            </div>

            <div className={`form-group-conditional ${!isLogin ? 'show' : 'hide'}`}>
              <div className="form-group">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  autoCapitalize="none"
                  spellCheck={false}
                  enterKeyHint="go"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('confirmPassword')}
                  required={!isLogin}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? "Masquer la confirmation du mot de passe" : "Afficher la confirmation du mot de passe"}
                >
                  <i className={showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'} aria-hidden="true"></i>
                </button>
              </div>
            </div>

            {/* Se souvenir de moi - uniquement en mode connexion */}
            {isLogin && (
              <div className="remember-me-container">
                <label className="neon-checkbox">
                  <input 
                    type="checkbox" 
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <div className="neon-checkbox__frame">
                    <div className="neon-checkbox__box">
                      <div className="neon-checkbox__check-container">
                        <svg viewBox="0 0 24 24" className="neon-checkbox__check">
                          <path d="M3,12.5l7,7L21,5"></path>
                        </svg>
                      </div>
                      <div className="neon-checkbox__glow"></div>
                      <div className="neon-checkbox__borders">
                        <span></span><span></span><span></span><span></span>
                      </div>
                    </div>
                    <div className="neon-checkbox__effects">
                      <div className="neon-checkbox__particles">
                        <span></span><span></span><span></span><span></span>
                        <span></span><span></span><span></span><span></span>
                        <span></span><span></span><span></span><span></span>
                      </div>
                      <div className="neon-checkbox__rings">
                        <div className="ring"></div>
                        <div className="ring"></div>
                        <div className="ring"></div>
                      </div>
                      <div className="neon-checkbox__sparks">
                        <span></span><span></span><span></span><span></span>
                      </div>
                    </div>
                  </div>
                </label>
                <span className="remember-me-label">{t('rememberMe') || 'Se souvenir de moi'}</span>
              </div>
            )}

            {loading && isLogin ? (
              <div className="login-loader-container">
                <div className="preloader preloader-small">
                  <svg className="cart cart-small" role="img" aria-label="Shopping cart line animation" viewBox="0 0 128 128" width="64px" height="64px" xmlns="http://www.w3.org/2000/svg">
                    <g fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="8">
                      <g className="cart__track" stroke="hsla(0,10%,10%,0.1)">
                        <polyline points="4,4 21,4 26,22 124,22 112,64 35,64 39,80 106,80" />
                        <circle cx="43" cy="111" r="13" />
                        <circle cx="102" cy="111" r="13" />
                      </g>
                      <g className="cart__lines" stroke="currentColor">
                        <polyline className="cart__top" points="4,4 21,4 26,22 124,22 112,64 35,64 39,80 106,80" strokeDasharray="338 338" strokeDashoffset="-338" />
                        <g className="cart__wheel1" transform="rotate(-90,43,111)">
                          <circle className="cart__wheel-stroke" cx="43" cy="111" r="13" strokeDasharray="81.68 81.68" strokeDashoffset="81.68" />
                        </g>
                        <g className="cart__wheel2" transform="rotate(90,102,111)">
                          <circle className="cart__wheel-stroke" cx="102" cy="111" r="13" strokeDasharray="81.68 81.68" strokeDashoffset="81.68" />
                        </g>
                      </g>
                    </g>
                  </svg>
                </div>
              </div>
            ) : (
              <button 
                type="submit" 
                className={`btn-liquid-glass ${loading && !isLogin ? 'btn-fade-out' : ''}`}
                disabled={loading}
              >
                <span className="btn-content">
                  {loading && !isLogin ? t('loading') : isLogin ? (
                    <img src="/Icon login.png" alt={t('login')} className="login-icon" />
                  ) : (
                    <img src="/Icon login.png" alt={t('createAccount')} className="login-icon" />
                  )}
                </span>
              </button>
            )}
          </form>

          <p className="login-footer">
            {t('loginFooter').split('\n').map((line, i) => (
              <span key={i}>
                {line}
                {i < t('loginFooter').split('\n').length - 1 && <br />}
              </span>
            ))}
          </p>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
